/**
 * ConfirmDialog (CONTENT section 15): a verb-plus-object title in Newsreader, one true
 * consequence sentence in Literata, and the verb repeated on the confirming button, red
 * only when the action is destructive. `children` hosts an extra field (a lingkungan
 * select, a reason) between the sentence and the buttons. While the action runs (`sibuk`)
 * both buttons stay focusable (`aria-disabled`, the `SIBUK` look), a second press of the verb
 * does nothing, and nothing closes the dialog (Batal, Escape, the backdrop), as DialogIsian;
 * `disabled` is for a choice not yet made. A caller whose question changes in place (the bulk
 * actions' second question) moves the focus to Batal (`batalRef`) and has Batal described by
 * the new title and sentence (`jelaskanBatal`), so the new question is read out.
 */
import { type ReactNode, type Ref, useId } from 'react'
import { SIBUK } from '@/components/primitives/Wizard'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  judul: string
  kalimat: string
  ya: string
  batal: string
  onYa: () => void | Promise<void>
  destruktif?: boolean | undefined
  /** Disables the confirming button (a required choice not yet made). */
  disabled?: boolean | undefined
  /** The action is running: the confirming button reads busy and ignores presses. */
  sibuk?: boolean | undefined
  /**
   * Where the focus goes when the dialog closes (Base UI's `finalFocus`): an element when the
   * one that opened it is gone (a row the action removed), else true for that one. Without it
   * Base UI's default stands (`fokusAkhirProps`).
   */
  fokusAkhir?: (() => HTMLElement | null | true) | undefined
  /** Batal itself: a caller moves the focus to it when the question changes in place. */
  batalRef?: Ref<HTMLButtonElement> | undefined
  /** Batal is described by the title and the sentence: the focus on it reads the question. */
  jelaskanBatal?: boolean | undefined
  children?: ReactNode
}

/**
 * Base UI's `finalFocus` for a dialog, only when the caller names where the focus goes. Base UI
 * treats any value that is not a boolean as explicit and returns the focus even when it has
 * moved on meanwhile (a dialog that opens another screen, as the photo consent does); without
 * one it leaves focus that moved elsewhere where it is.
 */
export function fokusAkhirProps(fokusAkhir: (() => HTMLElement | null | true) | undefined): {
  finalFocus?: () => HTMLElement | true
} {
  return fokusAkhir === undefined ? {} : { finalFocus: () => fokusAkhir() ?? true }
}

export function ConfirmDialog({
  open,
  onOpenChange,
  judul,
  kalimat,
  ya,
  batal,
  onYa,
  destruktif = false,
  disabled = false,
  sibuk = false,
  fokusAkhir,
  batalRef,
  jelaskanBatal = false,
  children,
}: ConfirmDialogProps) {
  const id = useId()
  const judulId = `${id}-judul`
  const kalimatId = `${id}-kalimat`
  return (
    <Dialog
      open={open}
      onOpenChange={(buka) => {
        if (buka || !sibuk) onOpenChange(buka)
      }}
    >
      <DialogContent showCloseButton={false} {...fokusAkhirProps(fokusAkhir)}>
        <DialogHeader>
          <DialogTitle id={judulId} className="font-display text-section font-normal">
            {judul}
          </DialogTitle>
          <DialogDescription id={kalimatId}>{kalimat}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button
            ref={batalRef}
            type="button"
            variant="ghost"
            className={SIBUK}
            aria-disabled={sibuk || undefined}
            aria-describedby={jelaskanBatal ? `${judulId} ${kalimatId}` : undefined}
            onClick={() => {
              if (!sibuk) onOpenChange(false)
            }}
          >
            {batal}
          </Button>
          <Button
            variant={destruktif ? 'destructive' : 'default'}
            className={SIBUK}
            disabled={disabled}
            aria-disabled={sibuk || undefined}
            onClick={() => {
              if (!sibuk) void onYa()
            }}
          >
            {ya}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
